import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FilterBar,
  FilterBarProps,
} from "~/components/Filter/FilterBar/FilterBar";
import {
  FilterForm,
  FilterFormProps,
} from "~/components/Filter/FilterForm/FilterForm";
import { QuickFilterBar } from "~/components/Filter/QuickFilterBar/QuickFilterBar";
import {
  QuickFilterButton,
  QuickFilterButtonProps,
} from "~/components/Filter/QuickFilterButton/QuickFilterButton";
import {
  ActiveFilterButton,
  ActiveFilterButtonProps,
} from "~/components/Filter/ActiveFilterButton/ActiveFilterButton";
import { ActiveFilterBar } from "~/components/Filter/ActiveFilterBar/ActiveFilterBar";
import { FilterMenuOption } from "~/components/Filter/FilterMenu";
import { ChevronRight as ChevronRightIcon } from "@mui/icons-material";
import { groupBy } from "lodash-es";
import {
  useGetAggregateScoresLazyQuery,
  AggregateScoreAggregationType,
  AggregateScoreType,
} from "~/operations";
import { organizationMrnFromId } from "~/hooks/useScope";
import { PLATFORM_POLICY_MRN } from "~/lib/mrn";
import { ScopeType } from "./useEntityScopes";

export type EntityScopesFilterVariables = {
  queryTerms?: string[];
  owners?: string[];
};

export type UseEntityScopesFilterContextParams = {
  scopeType: ScopeType;
  findingMrn: string | undefined;
  scoreType?: AggregateScoreType;
  hideOrganizationFilters?: boolean;
};

export type UseEntityScopesFilterContextOutput = {
  filterVariables: EntityScopesFilterVariables;
  filterForm: ReactNode;
  filterBar: ReactNode;
  quickFilterBar: ReactNode;
  activeFilterBar: ReactNode;
  activeFilterCount: number;
  resetFilters: () => void;
};

export function useEntityScopesFilterContext({
  scopeType,
  findingMrn,
  scoreType,
  hideOrganizationFilters = false,
}: UseEntityScopesFilterContextParams): UseEntityScopesFilterContextOutput {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState("");

  // Read filter params from URL
  const searchFilterParams = searchParams.getAll("queryTerm");
  const ownerFilterParams = searchParams.getAll("owner");
  const organizationFilterParams = searchParams.getAll("organization");
  const spaceFilterParams = searchParams.getAll("space");

  // Memoize filter arrays to prevent unnecessary re-renders.
  // searchParams.getAll() returns a new array reference on every render,
  // so we use join() to create a stable primitive for comparison.
  const searchFilters = useMemo(
    () => (searchFilterParams.length ? searchFilterParams : undefined),
    [searchFilterParams.join(",")],
  );
  const ownerFilters = useMemo(
    () => (ownerFilterParams.length ? ownerFilterParams : undefined),
    [ownerFilterParams.join(",")],
  );
  const organizationFilters = useMemo(
    () =>
      organizationFilterParams.length ? organizationFilterParams : undefined,
    [organizationFilterParams.join(",")],
  );
  const spaceFilters = useMemo(
    () => (spaceFilterParams.length ? spaceFilterParams : undefined),
    [spaceFilterParams.join(",")],
  );

  const resetCursorUrl = (href: string) => new URL(href);

  const addFilter = (param: string, value: string, multiple = false) => {
    const url = resetCursorUrl(location.href);
    if (multiple) {
      if (!url.searchParams.has(param, value)) {
        url.searchParams.append(param, value);
      }
    } else {
      url.searchParams.set(param, value);
    }
    navigate(url.href.replace(url.origin, ""));
  };

  const removeFilter = (param: string, value?: string) => {
    const url = resetCursorUrl(location.href);
    url.searchParams.delete(param, value);
    navigate(url.href.replace(url.origin, ""));
  };

  const resetFilters = () => {
    const url = resetCursorUrl(location.href);
    url.searchParams.delete("queryTerm");
    url.searchParams.delete("owner");
    url.searchParams.delete("organization");
    url.searchParams.delete("space");
    navigate(url.href.replace(url.origin, ""));
  };

  const activeFilters = [
    searchFilters
      ? searchFilters.map((searchFilter) => ({
          label: "Search",
          value: searchFilter,
          onClick: () => removeFilter("queryTerm", searchFilter),
        }))
      : [],
    ownerFilters
      ? ownerFilters.map((ownerFilter) => ({
          label: "Owner",
          value: ownerFilter,
          onClick: () => removeFilter("owner", ownerFilter),
        }))
      : [],
    organizationFilters
      ? organizationFilters.map((orgFilter) => ({
          label: "Organization",
          value: orgFilter,
          onClick: () => removeFilter("organization", orgFilter),
        }))
      : [],
    spaceFilters
      ? spaceFilters.map((spaceFilter) => ({
          label: "Space",
          value: spaceFilter,
          onClick: () => removeFilter("space", spaceFilter),
        }))
      : [],
  ].flatMap((f) => f);

  const [focusedField, setFocusedField] = useState<FilterMenuOption>();

  const fieldOptions = useEntityScopeSuggestions(
    focusedField?.field || "",
    searchInput,
    scopeType,
    findingMrn,
    scoreType,
  );

  useEffect(() => {
    if ("fetchSuggestions" in fieldOptions) {
      fieldOptions.fetchSuggestions();
    }
  }, [focusedField?.field]);

  const filterBar = renderFilterBar({
    fieldOptions: fieldOptions.suggestions,
    focusedField,
    inputValue: searchInput,
    onInputValueChange: (value: string) => {
      setSearchInput(value);
    },
    onFocusedRemove: () => {
      setFocusedField(undefined);
    },
    onFilterSelect: (filter) => {
      setSearchInput("");
      if (!focusedField) {
        setFocusedField(filter);
      } else {
        setFocusedField(undefined);
        if (filter.param) {
          addFilter(filter.param, filter.value, filter.isMultiple);
        }
      }
    },
    onFilterCreate: (filter) => {
      setFocusedField(undefined);
      setSearchInput("");
      addFilter("queryTerm", filter, true);
    },
  });

  const ownerSuggestions = useOwnerSuggestions();
  const organizationSuggestions = useOrganizationSuggestions(
    findingMrn,
    scoreType,
  );
  const spaceSuggestions = useSpaceSuggestions(findingMrn, scoreType);

  const quickMenuOptions = (param: string, options: FilterMenuOption[]) => {
    return options.map((o) => ({
      ...o,
      onSelect: () => addFilter(param, o.value, o.isMultiple),
    }));
  };

  // Build quick filters based on scope type
  const quickFilters: QuickFilterButtonProps[] = [];

  // Owners filter - available for all scope types. Hidden for now until we have owners data.
  // quickFilters.push({
  //   label: "Owners",
  //   activeCount: ownerFilters?.length || 0,
  //   menuOptions: quickMenuOptions("owner", ownerSuggestions.suggestions),
  //   onMenuOpen: ownerSuggestions.fetchSuggestions,
  // });

  // Organization filter - only for Spaces and Workspaces
  if (
    (scopeType === "Spaces" || scopeType === "Workspaces") &&
    !hideOrganizationFilters
  ) {
    quickFilters.push({
      label: "Organization",
      activeCount: organizationFilters?.length || 0,
      menuOptions: quickMenuOptions(
        "organization",
        organizationSuggestions.suggestions,
      ),
      onMenuOpen: organizationSuggestions.fetchSuggestions,
    });
  }

  // Space filter - only for Workspaces
  if (scopeType === "Workspaces") {
    quickFilters.push({
      label: "Space",
      activeCount: spaceFilters?.length || 0,
      menuOptions: quickMenuOptions("space", spaceSuggestions.suggestions),
      onMenuOpen: spaceSuggestions.fetchSuggestions,
    });
  }

  const quickFilterBar = renderQuickFilterBar(quickFilters);

  const activeFilterBar = renderActiveFilterBar(activeFilters, resetFilters);

  const activeFilterCount = activeFilters.length;

  const filterForm = renderFilterForm({
    filterBar,
    quickFilterBar,
    activeFilterBar,
  });

  const filterVariables: EntityScopesFilterVariables = useMemo(() => {
    return {
      ...(searchFilters?.length ? { queryTerms: searchFilters } : {}),
      ...(ownerFilters?.length ? { owners: ownerFilters } : {}),
    };
  }, [searchFilters, ownerFilters]);

  return {
    filterVariables,
    filterForm,
    filterBar,
    quickFilterBar,
    activeFilterBar,
    activeFilterCount,
    resetFilters,
  };
}

function renderFilterBar(props: FilterBarProps) {
  return <FilterBar {...props} />;
}

function renderQuickFilterBar(quickFilters: QuickFilterButtonProps[]) {
  return (
    <QuickFilterBar>
      {quickFilters.map((quickFilterProps) => {
        const key = quickFilterProps.label;
        return <QuickFilterButton key={key} {...quickFilterProps} />;
      })}
    </QuickFilterBar>
  );
}

function renderActiveFilterBar(
  activeFilters: ActiveFilterButtonProps[],
  onResetClick: () => void,
) {
  const items = Object.entries(groupBy(activeFilters, "label")).flatMap(
    ([label, values], index) => {
      const buttons = values.map((props) => {
        const key = props.label + props.value;
        return <ActiveFilterButton key={key} {...props} />;
      });
      const separator = (
        <ChevronRightIcon
          key={label + "-separator"}
          sx={{ fontSize: "16px", color: "text.secondary" }}
        />
      );
      return [index === 0 ? [] : separator, ...buttons];
    },
  );
  return (
    items.length > 0 && (
      <ActiveFilterBar onResetClick={onResetClick}>{items}</ActiveFilterBar>
    )
  );
}

function renderFilterForm(props: FilterFormProps) {
  return <FilterForm {...props} />;
}

function useEntityScopeSuggestions(
  field: string,
  searchInput: string,
  scopeType: ScopeType,
  findingMrn: string | undefined,
  scoreType?: AggregateScoreType,
) {
  const categorySuggestions = useCategorySuggestions(searchInput, scopeType);
  const ownerSuggestions = useOwnerSuggestions();
  const organizationSuggestions = useOrganizationSuggestions(
    findingMrn,
    scoreType,
  );
  const spaceSuggestions = useSpaceSuggestions(findingMrn, scoreType);

  switch (field) {
    case "owner":
      return ownerSuggestions;
    case "organization":
      return organizationSuggestions;
    case "space":
      return spaceSuggestions;
    default:
      return categorySuggestions;
  }
}

function useCategorySuggestions(searchInput: string, scopeType: ScopeType) {
  const ownerFilterGroup: FilterMenuOption = {
    field: "owner",
    label: "Owner",
    value: "owner",
  };

  const organizationFilterGroup: FilterMenuOption = {
    field: "organization",
    label: "Organization",
    value: "organization",
  };

  const spaceFilterGroup: FilterMenuOption = {
    field: "space",
    label: "Space",
    value: "space",
  };

  // Build available filters based on scope type
  // Owners filter hidden for now until we have owners data
  const availableFilters: FilterMenuOption[] = [];

  if (scopeType === "Spaces" || scopeType === "Workspaces") {
    availableFilters.push(organizationFilterGroup);
  }

  if (scopeType === "Workspaces") {
    availableFilters.push(spaceFilterGroup);
  }

  const suggestions = availableFilters.filter((s) =>
    s.label.toLocaleLowerCase().includes(searchInput.toLocaleLowerCase()),
  );

  return {
    suggestions,
    loading: false,
    error: false,
  };
}

/**
 * Hook to get owner suggestions for entity scopes.
 * TODO: Implement with real endpoint when owner data is available in aggregateScores.
 */
function useOwnerSuggestions() {
  const param = "owner";

  const fetchSuggestions = useCallback(() => {
    // No-op for now
  }, []);

  const emptySuggestion = {
    param,
    field: `${param}.empty`,
    value: "",
    label: "No results",
  };

  return {
    suggestions: [emptySuggestion],
    fetchSuggestions,
    loading: false,
    error: undefined,
  };
}

/**
 * Hook to get organization suggestions for entity scopes.
 * Uses aggregateScores with ORGANIZATION aggregation type.
 */
function useOrganizationSuggestions(
  findingMrn: string | undefined,
  scoreType?: AggregateScoreType,
) {
  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAggregateScoresLazyQuery({
      fetchPolicy: "cache-and-network",
    });

  const fetchSuggestions = useCallback(() => {
    if (findingMrn) {
      return getSuggestions({
        variables: {
          entityMrn: PLATFORM_POLICY_MRN,
          filter: {
            findingMrn: findingMrn,
            aggregationType: AggregateScoreAggregationType.Organization,
            ...(scoreType && { scoreType }),
          },
          skipCases: true,
        },
      });
    }
  }, [findingMrn, scoreType, getSuggestions]);

  const param = "organization";

  const suggestionsData = useMemo(() => {
    const queryData = data ?? previousData;
    if (
      !queryData?.aggregateScores ||
      queryData.aggregateScores.__typename !== "AggregateScoresConnection"
    ) {
      return [];
    }

    return (
      queryData.aggregateScores.edges
        ?.map((edge, index) => {
          const entity = edge.node?.entity as any;
          if (entity?.__typename !== "EntityInfoOrganization") return null;

          return {
            param,
            field: `${param}.${index}`,
            value: entity.id,
            label: entity.id || entity.name,
            isMultiple: true,
          } as FilterMenuOption;
        })
        .filter((item): item is FilterMenuOption => item !== null) || []
    );
  }, [data, previousData]);

  const loadingSuggestion = {
    param,
    field: `${param}.loading`,
    value: "",
    label: "Loading...",
  };
  const emptySuggestion = {
    param,
    field: `${param}.empty`,
    value: "",
    label: "No results",
  };

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}

function useSpaceSuggestions(
  findingMrn: string | undefined,
  scoreType?: AggregateScoreType,
) {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get("organizationId");

  const entityMrn = organizationId
    ? organizationMrnFromId(organizationId)
    : PLATFORM_POLICY_MRN;

  const [getSuggestions, { data, loading, error, previousData }] =
    useGetAggregateScoresLazyQuery({
      fetchPolicy: "cache-and-network",
    });

  const fetchSuggestions = useCallback(() => {
    if (findingMrn) {
      return getSuggestions({
        variables: {
          entityMrn,
          filter: {
            findingMrn: findingMrn,
            aggregationType: AggregateScoreAggregationType.Space,
            ...(scoreType && { scoreType }),
          },
          skipCases: true,
        },
      });
    }
  }, [findingMrn, scoreType, entityMrn, getSuggestions]);

  const param = "space";

  const suggestionsData = useMemo(() => {
    const queryData = data ?? previousData;
    if (
      !queryData?.aggregateScores ||
      queryData.aggregateScores.__typename !== "AggregateScoresConnection"
    ) {
      return [];
    }

    return (
      queryData.aggregateScores.edges
        ?.map((edge, index) => {
          const entity = edge.node?.entity as any;
          if (entity?.__typename !== "EntityInfoSpace") return null;

          return {
            param,
            field: `${param}.${index}`,
            value: entity.id,
            label: entity.name || entity.id,
            isMultiple: true,
          } as FilterMenuOption;
        })
        .filter((item): item is FilterMenuOption => item !== null) || []
    );
  }, [data, previousData]);

  const loadingSuggestion = {
    param,
    field: `${param}.loading`,
    value: "",
    label: "Loading...",
  };
  const emptySuggestion = {
    param,
    field: `${param}.empty`,
    value: "",
    label: "No results",
  };

  const suggestions =
    suggestionsData.length > 0
      ? suggestionsData
      : loading
        ? [loadingSuggestion]
        : [emptySuggestion];

  return {
    suggestions,
    fetchSuggestions,
    loading,
    error,
  };
}
